import React from 'react';
import { Box, Button } from '@material-ui/core';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSelector } from 'react-redux';

import { darkPurple } from 'utils/color-styles';
import { ModalTitle, ModalTypography } from 'components/Modal/Modal';
import { ModalStep } from 'components/Flow/components/ModalStep';
import { DeedHeartHands } from 'components/Flow/components/DeedHeartHands';
import { StepComponentProps } from 'components/Flow/RenderMachine';
import { selectOrganization } from 'entities/user/selectors';
import config from 'config';

export const PayPalFailure = ({ send, state }: StepComponentProps): JSX.Element => {
  const organization = useSelector(selectOrganization);
  const {
    verifyWithPayPalServiceErrorCode,
    verifyWithPayPalServiceNamesAreSimilar,
    verifyWithPayPalServiceBusinessName,
  } = state.context;

  const verifyAccountLocation =
    'https://www.paypal.com/myaccount/money/flow/banks/new?flow=eyJyZXR1cm5VcmwiOiIvbWVwL2Rhc2hib2FyZCIsImNhbmNlbFVybCI6Ii9tZXAvZGFzaGJvYXJkIiwiZmxhZ3MiOlsiaXNJbk1vbmV5V2l0aENob2ljZSIsImlzSW5BdXRvTGlua0JhbmsiXX0=';

  const contactHelpCenterLocation = `https://www.paypal.com/${
    organization?.locationObject?.countryCode?.toLowerCase() || 'us'
  }/cshelp/personal`;

  const isUserAccountVerifiedError =
    verifyWithPayPalServiceErrorCode === 'PayPalUserAccountNotVerified';

  const ctaButtonLocation = isUserAccountVerifiedError
    ? verifyAccountLocation
    : contactHelpCenterLocation;

  const ctaButtonCopy = isUserAccountVerifiedError
    ? 'Go to PayPal to Link Bank Account'
    : 'Contact Help Center';

  const renderErrorMessage = () => {
    if (verifyWithPayPalServiceErrorCode === 'PayPalInvalidGrant') {
      return (
        <>
          Your PayPal verification was unsuccessful due to the authorization attempt expiring.
          Please try again.
        </>
      );
    }

    if (isUserAccountVerifiedError) {
      return (
        <>
          Your PayPal account is not fully verified. To complete verification, please return to
          PayPal and link your bank account. Once your bank account is linked, try verifying again.
        </>
      );
    }

    if (verifyWithPayPalServiceErrorCode === 'PayPalOrganizationNameMissing') {
      return (
        <>
          Your PayPal verification failed. Please ensure you’re using the credentials associated
          with your organization’s PayPal business account for verification.
        </>
      );
    }

    if (verifyWithPayPalServiceErrorCode === 'PayPalOrganizationNameMismatch') {
      if (verifyWithPayPalServiceNamesAreSimilar) {
        return (
          <>
            Your application is under manual review due to a mismatch between your organization’s
            business name on PayPal and the name of the organization you’re trying to claim. The
            nonprofit team will reach out shortly to move you forward in the process. In the
            meantime, you’re welcome to retry.
          </>
        );
      }

      return (
        <>
          Your organization’s PayPal business name, “{verifyWithPayPalServiceBusinessName}”, doesn’t
          match the name of the organization you’re trying to claim on Deed. Please make sure that
          you’re using the right PayPal business account or that you’re claiming the right
          organization on Deed and try again.
          <span style={{ marginTop: '10px', display: 'block' }}></span>If you’re sure this is the
          right business account, please contact our{' '}
          <a href={`mailto:${config.nonprofitSuccessTeamEmail}`}>Nonprofit Success Team</a> for
          further assistance and ensure you provide, your PayPal organization name, PayPal user, and
          organization legal name and EIN.
        </>
      );
    }

    return (
      <>
        Your PayPal verification was unsuccessful. Please reach out to the owner of the PayPal
        account to get verified.
      </>
    );
  };

  return (
    <ModalStep
      showProgressBar
      actions={
        <div style={{ color: darkPurple }}>
          <Button
            type="button"
            endIcon={<ArrowBackIcon style={{ fontSize: 16 }} />}
            style={{ marginRight: 8 }}
            onClick={() => send('RETRY')}
          >
            Try again
          </Button>

          <Button
            type="button"
            variant="contained"
            style={{ marginRight: 8 }}
            onClick={() =>
              send({
                type: 'FOLLOW',
                location: ctaButtonLocation,
              })
            }
          >
            {ctaButtonCopy}
          </Button>
        </div>
      }
      header={<DeedHeartHands />}
      headerStyles={{
        paddingTop: 16,
      }}
    >
      <Box mb={4}>
        <ModalTitle size="large">Your verification was unsuccessful</ModalTitle>
        <ModalTypography>{renderErrorMessage()}</ModalTypography>
      </Box>
    </ModalStep>
  );
};
